import { hasModule, type AppModule } from "@/lib/modules"
import { reportCatalog, type ReportCategory, type ReportItem } from "@/lib/report-catalog"
import { filterVisibleReports, type ReportVisibilityMap } from "@/lib/report-visibility"

export type ReportHubCategory = ReportCategory | "customers" | "suppliers"

const HUB_ORDER: ReportHubCategory[] = ["pos", "sales", "customers", "stock", "purchase", "suppliers"]

const POS_APIS = new Set(reportCatalog.pos.map((r) => r.api))

const CUSTOMER_APIS = new Set([
  "/api/reports/sales/clients-list",
  "/api/reports/sales/client-statements",
  "/api/reports/sales/by-client",
  "/api/reports/sales/client-sales-product",
  "/api/reports/sales/product-sales-client",
  "/api/reports/sales/trading-terms",
])

const SUPPLIER_APIS = new Set([
  "/api/reports/purchase/suppliers-list",
  "/api/reports/purchase/supplier-statements",
  "/api/reports/purchase/trading-terms",
])

const HUB_MODULE: Record<ReportHubCategory, AppModule | null> = {
  pos: null,
  sales: "sales",
  customers: "sales",
  stock: "stock",
  purchase: "purchasing",
  suppliers: "purchasing",
}

export function catalogCategoryForHub(hub: ReportHubCategory): ReportCategory {
  if (hub === "customers") return "sales"
  if (hub === "suppliers") return "purchase"
  return hub
}

export function reportsForHub(hub: ReportHubCategory): ReportItem[] {
  const items = reportCatalog[catalogCategoryForHub(hub)]
  if (hub === "customers") return items.filter((r) => CUSTOMER_APIS.has(r.api))
  if (hub === "sales") return items.filter((r) => !CUSTOMER_APIS.has(r.api) && !POS_APIS.has(r.api))
  if (hub === "suppliers") return items.filter((r) => SUPPLIER_APIS.has(r.api))
  if (hub === "purchase") return items.filter((r) => !SUPPLIER_APIS.has(r.api))
  return items
}

export function canAccessHubCategory(hub: ReportHubCategory, modules?: string[]) {
  const required = HUB_MODULE[hub]
  return required == null || hasModule(modules, required)
}

export function isHubCategoryVisible(
  hub: ReportHubCategory,
  modules?: string[],
  visibility?: ReportVisibilityMap | null,
) {
  if (!canAccessHubCategory(hub, modules)) return false
  return filterVisibleReports(reportsForHub(hub), visibility).length > 0
}

export function firstVisibleHubCategory(
  modules?: string[],
  visibility?: ReportVisibilityMap | null,
): ReportHubCategory | null {
  return HUB_ORDER.find((hub) => isHubCategoryVisible(hub, modules, visibility)) ?? null
}

export function canAccessReportApi(api: string, modules?: string[]) {
  if (api.startsWith("/api/reports/stock/")) return hasModule(modules, "stock")
  if (api.startsWith("/api/reports/purchase/")) return hasModule(modules, "purchasing")
  if (api.startsWith("/api/reports/sales/")) return hasModule(modules, "sales")
  return true
}
